import { Box, Divider, MenuItem, Skeleton, Stack, useTheme } from '@mui/material';
import { FC, memo, useEffect, useMemo, useState, MouseEvent, useCallback } from 'react';
import ReactGA from 'react-ga4';
import StyledMenu from 'src/components/StyledMenu';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import { PlanningsDeliveryEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';
import { ShipmentOrderLegRunFactory } from 'src/models/ShipmentOrderLegRunFactory';

import DisplayPropertiesSelectableUnallocatedOrderPresenter
  from './DisplayPropertiesSelectableUnallocatedOrder.presenter';

type Props = {
  id: number;
  data: ShipmentOrderLegRunForPlanEntity;
  addSelectedId: (id: number) => void;
  removeSelectedId: (id: number) => void;
  selected: boolean;
  isLoading: boolean;
  setEditShipmentOrderId: (id: number) => void;
  setEditShipmentDialogOpen: (open: boolean) => void;
  setSplitDialogIsOpen: (open: boolean) => void;
  mutateDeleteSpecificOrder: (orderId: number) => void;
  mutateCloneOrder: (shipimentOrderId: number) => void;
  customInputFields: string[];
  hideOrderProperties: string[];
  truckEntities: PlanningsTruckEntity[];
  driverEntities: PlanningsDriverEntity[];
  deliveryEntities: PlanningsDeliveryEntity[];
  mutateRestoreSplittedOrder: (orderId: number) => void;
  mutateRestoreSplittedOrders: () => void;
}

const OrderPresenter: FC<Props> = memo((
  {
    id,
    data,
    addSelectedId,
    removeSelectedId,
    selected,
    isLoading,
    setEditShipmentOrderId,
    setEditShipmentDialogOpen,
    setSplitDialogIsOpen,
    mutateDeleteSpecificOrder,
    mutateCloneOrder,
    customInputFields,
    hideOrderProperties,
    truckEntities,
    driverEntities,
    deliveryEntities,
    mutateRestoreSplittedOrder,
    mutateRestoreSplittedOrders,
  }
) => {
  const theme = useTheme();
  const [model, setModel] = useState<ShipmentOrderLegRunFactory | undefined>(undefined);
  useEffect(() => {
    if (!data) return;

    setModel(
      new ShipmentOrderLegRunFactory(data)
    );
  }, [data]);

  const [bgColor, setBgColor] = useState<string>(theme.colors.alpha.black[10]);
  useEffect(() => {
    if (selected) {
      setBgColor(theme.colors.primary.light);

      return;
    }

    setBgColor(theme.colors.alpha.black[10]);
  }, [selected, theme.colors]);

  const onClick = useCallback(() => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: `案件選択 ${selected ? 'OFF' : 'ON'}`, label: id });
    if (isLoading) return;

    if (selected) {
      removeSelectedId(id);
    } else {
      addSelectedId(id);
    }
  }, [addSelectedId, id, isLoading, removeSelectedId, selected]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const onClickMenu = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);
  const onClickClose = useCallback((event: MouseEvent) => {
    event.stopPropagation();
    setAnchorEl(null);
  }, []);

  const onClickEdit = useCallback((event: MouseEvent) => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: '案件編集', label: id });
    event.stopPropagation();
    setAnchorEl(null);
    setEditShipmentOrderId(data.shipment_order_id);
    setEditShipmentDialogOpen(true);
  }, [data, setEditShipmentDialogOpen, setEditShipmentOrderId]);

  const onClickSplit = useCallback((event: MouseEvent) => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: '案件分割', label: id });
    event.stopPropagation();
    setAnchorEl(null);
    setEditShipmentOrderId(data.shipment_order_id);
    setSplitDialogIsOpen(true);
  }, [data, setSplitDialogIsOpen, setEditShipmentOrderId]);

  const onClickClone = useCallback((event: MouseEvent) => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: '案件コピー', label: id });
    event.stopPropagation();
    setAnchorEl(null);
    if (window.confirm('案件のコピーを作成します。よろしいですか？')) {
      mutateCloneOrder(data.shipment_order_id)
    }
  }, [id, data, mutateCloneOrder]);

  const onClickDelete = useCallback((event: MouseEvent) => {
    ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: '案件削除', label: id });
    event.stopPropagation();
    setAnchorEl(null);
    if (window.confirm('案件を削除します。よろしいですか？')) {
      mutateDeleteSpecificOrder(id);
    }
  }, [id, mutateDeleteSpecificOrder]);

  const orderMenuMemo = useMemo(() => (
    <StyledMenu
      anchorEl={anchorEl}
      open={open}
      onClose={onClickClose}
    >
      <MenuItem
        onClick={onClickEdit}
        disableRipple
      >
        編集
      </MenuItem>
      {/* <MenuItem
        onClick={onClickSplit}
        disableRipple
      >
        分割
      </MenuItem> */}
      <MenuItem
        onClick={onClickClone}
        disableRipple
      >
        コピー
      </MenuItem>
      <MenuItem
        onClick={onClickDelete}
        disableRipple
      >
        削除
      </MenuItem>
    </StyledMenu>
  ), [anchorEl, onClickClone, onClickClose, onClickDelete, onClickEdit, open]);

  const orderMemo = useMemo(() => (
    <DisplayPropertiesSelectableUnallocatedOrderPresenter
      data={data}
      customInputFields={customInputFields}
      onClickMenu={onClickMenu}
      hideOrderProperties={hideOrderProperties}
      truckEntities={truckEntities}
      driverEntities={driverEntities}
      deliveryEntities={deliveryEntities}
      mutateRestoreSplittedOrder={mutateRestoreSplittedOrder}
      mutateRestoreSplittedOrders={mutateRestoreSplittedOrders}
    />
  ), [data, customInputFields, onClickMenu, hideOrderProperties, truckEntities, driverEntities, deliveryEntities, mutateRestoreSplittedOrder, mutateRestoreSplittedOrders]);

  if (!model) return <Skeleton variant="rectangular" animation="wave" width="100%" height={145} />;

  return (
    <>
      <Box
        key={['OrderPresenter', 'Box', data.id].join('-')}
        onClick={onClick}
        sx={{
          width: '100%',
          textAlign: 'left',
          display: 'block',
          mb: 0.1,
          position: 'relative',
          cursor: 'pointer',
        }}
      >
        <Stack
          key={['OrderPresenter', 'Stack', data.id].join('-')}
          direction="row"
          alignItems="stretch"
          spacing={0}
          sx={{
            bgcolor: bgColor,
          }}
          pl={0.5}
        >
          {orderMemo}
          {orderMenuMemo}
        </Stack>
      </Box>
      <Divider sx={{ height: '1px' }} />
    </>
  );
});

export default OrderPresenter;
